import { paginationTableProps, tableStandardProps } from "@/utils/componentUtils";
import { formatLocaleCurrency, formatLocaleDateTime } from "@/utils/formatUtils";
import { useFilterSearchParams } from "@/utils/hooksUtils";
import { URLQuery } from "@/utils/queryUtils";
import { numberOrZero } from "@/utils/utils";
import { Button, Col, Divider, Popconfirm, Row, Space, Switch, Table } from "antd";
import { ColumnsType } from "antd/lib/table";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import { useDeleteManualDebtAccountMovement, useFilterDebtAccountMovements } from "../../../../../common/api/queries";
import ActionTextIcon from "../../../../../common/components/icons/ActionTextIcon";
import AntIcon from "../../../../../common/components/icons/AntIcon";
import PopconfirmDeleteIcon from "../../../../../common/components/icons/PopconfirmDeleteIcon";
import Ellipsis from "../../../../../common/components/views/Ellipsis";
import { PageSizes, rowGutter, TableSizes } from "../../../../../common/constants";
import type { UUID } from "../../../../../typings/global";
import { ManualMovementTag } from "../../../bailaccount/components/ManualMovementTag";
import NotFinishedBatchTag from "../../../batch/components/NotFinishedBatchTag";
import { CommissionsBatchStep } from "../../../batch/enums";
import { CommissionBatchName } from "../../../components/CommissionBatchName";
import { CommissionsCategory } from "../../../enums";
import { DebtAccountMovement } from "../../types";
import { DebtAccountMovementModalForm } from "../forms/DebtAccountMovementModalForm";

interface Props {
  agentId: UUID;
  category: CommissionsCategory;
  showFullBatchNameWithLink?: boolean;
  showFilterSwitch: boolean;
  showActions?: boolean;
}

export const DebtAccountMovementTable = ({
  agentId,
  showFullBatchNameWithLink,
  showFilterSwitch,
  category,
  showActions,
}: Props) => {
  const { t } = useTranslation();

  const navigate = useNavigate();

  const [onlyProcessed, setOnlyProcessed] = useState(false);
  const [onlyManual, setOnlyManual] = useState(false);
  const [isDebtAccountMovementFormOpen, setIsDebtAccountMovementFormOpen] = useState(false);
  const [debtAccountMovementToUpdate, setDebtAccountMovementToUpdate] = useState<DebtAccountMovement>();

  const searchParams = useFilterSearchParams({ pageSize: PageSizes.MEDIUM });

  const { data: movementsPage } = useFilterDebtAccountMovements({
    id: agentId,
    object: { ...searchParams, onlyProcessed, onlyManual, category },
  });

  const deleteManualDebtAccountMovement = useDeleteManualDebtAccountMovement();

  const columns = useMemo(() => {
    const items: ColumnsType<DebtAccountMovement> = [
      {
        key: "commissionsBatch",
        title: t("commissions.debtAccount.attrs.movements.commissionsBatch"),
        width: 350,
        ellipsis: { showTitle: false },
        render: (_, record) =>
          record.commissionsBatch ? (
            <CommissionBatchName
              batchName={record.commissionsBatch?.name ?? ""}
              batchId={record.commissionsBatch?.id ?? ""}
              showLink={showFullBatchNameWithLink}
            />
          ) : (
            <Ellipsis className="sub-header-info dashed">{t("commissions.debtAccount.helpers.noBatch")}</Ellipsis>
          ),
      },
      {
        key: "batchStage",
        width: 150,
        render: (_, record) =>
          record.commissionsBatch &&
          record.commissionsBatch.step !== CommissionsBatchStep.FINISH && <NotFinishedBatchTag />,
      },
      {
        key: "createdAt",
        title: t("common.createdAt"),
        width: 160,
        render: (_, record) => <span>{formatLocaleDateTime(record.createdAt)}</span>,
      },
      {
        key: "manual",
        width: 140,
        render: (_, record) => <ManualMovementTag isManual={record.manual} note={record.note} />,
      },
      {
        key: "amount",
        title: t("common.amount"),
        align: "right",
        fixed: "right",
        width: 150,
        render: (_, record) => <span>{formatLocaleCurrency(record.amount)}</span>,
      },
    ];

    if (showActions) {
      items.push({
        key: "actions",
        align: "right",
        fixed: "right",
        width: 200,
        render: (_, record) =>
          record.manual &&
          !record.commissionsBatch && (
            <>
              <ActionTextIcon
                icon="edit"
                color="blue"
                text={t("common.edit")}
                onClick={() => {
                  setDebtAccountMovementToUpdate(record);
                  setIsDebtAccountMovementFormOpen(true);
                }}
              />

              <Divider orientation="vertical" />

              <Popconfirm
                title={t("commissions.debtAccount.helpers.deleteMovementConfirm")}
                icon={<PopconfirmDeleteIcon />}
                okText={t("common.yes")}
                cancelText={t("common.no")}
                okType="danger"
                onConfirm={() => deleteManualDebtAccountMovement.mutate({ id1: agentId, id2: record.id })}
              >
                <ActionTextIcon icon="delete" color="red" text={t("common.delete")} />
              </Popconfirm>
            </>
          ),
      });
    }

    return items;
  }, [showActions, showFullBatchNameWithLink, t]);

  return (
    <>
      <Row gutter={rowGutter} className="margin-bottom-tiny">
        <Col span={12}>
          <Space size="middle" style={{ height: "100%" }} align="end">
            <Space>
              <span>{t("commissions.debtAccount.helpers.filteredMovementsSum")}:</span>
              <b>{formatLocaleCurrency(movementsPage?.amountsSum ?? 0)}</b>
            </Space>

            {showActions && (
              <Button
                variant="outlined"
                color="primary"
                icon={<AntIcon type="plus-circle" />}
                size="small"
                onClick={() => setIsDebtAccountMovementFormOpen(true)}
              >
                {t("commissions.bailAccount.actions.createMovement")}
              </Button>
            )}
          </Space>
        </Col>

        {showFilterSwitch && (
          <Col span={12} className="right-align">
            <div>
              <span className="margin-right-tiny">{t("commissions.debtAccount.helpers.showOnlyProcessed")}</span>
              <Switch
                size="small"
                value={onlyProcessed}
                checkedChildren={<AntIcon type="check" />}
                unCheckedChildren={<AntIcon type="close" />}
                onChange={(value) => {
                  setOnlyProcessed(value);
                  navigate({ search: URLQuery.getTableChangePageIndex(0) }, { replace: true });
                }}
              />
            </div>

            <div>
              <span className="margin-right-tiny">{t("commissions.debtAccount.helpers.showOnlyManual")}</span>
              <Switch
                size="small"
                value={onlyManual}
                checkedChildren={<AntIcon type="check" />}
                unCheckedChildren={<AntIcon type="close" />}
                onChange={(value) => {
                  setOnlyManual(value);
                  navigate({ search: URLQuery.getTableChangePageIndex(0) }, { replace: true });
                }}
              />
            </div>
          </Col>
        )}
      </Row>

      <Row gutter={rowGutter}>
        <Col span={24}>
          <Table<DebtAccountMovement>
            {...tableStandardProps()}
            columns={columns}
            scroll={{ x: TableSizes.LARGE }}
            dataSource={movementsPage?.pageData}
            pagination={{
              ...paginationTableProps,
              placement: ["bottomEnd"],
              current: numberOrZero(movementsPage?.pageIndex) + 1,
              pageSize: movementsPage?.pageSize,
              total: movementsPage?.totalElementsCount,
              onChange: (pageIndex) =>
                navigate({ search: URLQuery.getTableChangePageIndex(pageIndex) }, { replace: true }),
            }}
          />
        </Col>
      </Row>

      <DebtAccountMovementModalForm
        agentId={agentId}
        movement={debtAccountMovementToUpdate}
        isOpen={isDebtAccountMovementFormOpen}
        category={category}
        onCancel={() => {
          setIsDebtAccountMovementFormOpen(false);
          setDebtAccountMovementToUpdate(undefined);
        }}
      />
    </>
  );
};
